import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { CircularProgress, Typography } from '@mui/material';
import { PUBLISH_CASHFLOW_TEMPLATE } from '../../operations';
import React, { useState } from 'react';
import { useMutation } from '@apollo/client';

import { AddCircleOutline as AddCircleOutlineIcon } from '@mui/icons-material';
import { AdminsPageShell } from '../../../../../../components/AdminsPageShell';
import { CCFTemplateModal } from '../CCFTemplateModal';
import { ErrorOutline as ErrorOutlineIcon } from '@mui/icons-material';
import { formatTimestamp } from '../../../../../../utils/helpers';
import { styles } from './styles';
import { truncate } from '../../../../../../utils/helpers';
import { useCashflowTemplates } from '@/hooks/useCashflowTemplates';

export const CCFTemplatesList = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [publishingId, setPublishingId] = useState(null);

  const { loading, error, templates, refetch } = useCashflowTemplates();

  const [publishTemplate, { loading: publishLoading }] = useMutation(PUBLISH_CASHFLOW_TEMPLATE, {
    onCompleted: () => {
      refetch();
      setPublishingId(null);
    },
    onError: (error) => {
      console.error('Error publishing template:', error);
      setPublishingId(null);
    },
  });

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSuccess = () => {
    refetch();
  };

  const handlePublish = (id) => {
    setPublishingId(id);
    publishTemplate({
      variables: { id },
    });
  };

  return (
    <AdminsPageShell
      title="CCF Templates"
      actions={
        <Button
          sx={styles.button}
          variant="contained"
          color="secondary"
          startIcon={<AddCircleOutlineIcon />}
          onClick={handleOpenModal}
        >
          Upload Template
        </Button>
      }
    >
      <Box sx={styles.container}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', padding: '2em' }}>
            <CircularProgress />
          </Box>
        )}
        {error && (
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2em' }}
          >
            <ErrorOutlineIcon color="error" sx={{ marginRight: '0.5em' }} />
            <Typography color="error">Error loading templates: {error.message}</Typography>
          </Box>
        )}
        {!loading && !error && templates.length === 0 && (
          <Box sx={{ textAlign: 'center', padding: '2em' }}>
            <Typography>No templates found.</Typography>
          </Box>
        )}

        {templates && (
          <TableContainer component={Paper} sx={styles.tableContainer}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={styles.tableHeader}>Name</TableCell>
                  <TableCell sx={styles.tableHeader}>Description</TableCell>
                  <TableCell sx={styles.tableHeader}>Type</TableCell>
                  <TableCell sx={styles.tableHeader}>Country</TableCell>
                  <TableCell sx={styles.tableHeader}>Status</TableCell>
                  <TableCell sx={styles.tableHeader}>Created By</TableCell>
                  <TableCell sx={styles.tableHeader}>Updated At</TableCell>
                  <TableCell sx={styles.tableHeader}>Published At</TableCell>
                  <TableCell sx={styles.tableHeader}>Published By</TableCell>
                  <TableCell sx={styles.tableHeader}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell>{template.name}</TableCell>
                    <TableCell>{truncate(template.description, 30)}</TableCell>
                    <TableCell>{template.modelType?.toUpperCase()}</TableCell>
                    <TableCell>{template.country ? template.country.displayName : '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={template.isPublished ? 'Published' : 'Unpublished'}
                        sx={{
                          ...styles.chip,
                          ...(template.isPublished ? styles.published : styles.unpublished),
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {template.createdBy
                        ? `${template.createdBy.firstName} ${template.createdBy.lastName}`
                        : '-'}
                    </TableCell>
                    <TableCell>{formatTimestamp(template.updatedAt)}</TableCell>
                    <TableCell>
                      {template.isPublished ? formatTimestamp(template.publishedAt) : '-'}
                    </TableCell>
                    <TableCell>
                      {template.publishedBy
                        ? `${template.publishedBy.firstName} ${template.publishedBy.lastName}`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        disabled={template.isPublished || publishingId === template.id}
                        onClick={() => handlePublish(template.id)}
                        sx={{ ...styles.button, ...styles.actionButton }}
                      >
                        {publishLoading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          'Publish'
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <CCFTemplateModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={(templates) => handleSuccess(templates)}
      />
    </AdminsPageShell>
  );
};
